import {
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import useConfig from "hooks/useConfig";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import IHwrcSite from "interfaces/IHwrcSite";
import ICreateItemOrderRequestModel from "interfaces/Request/ICreateItemOrderRequestModel";
import { useStateMachine } from "little-state-machine";
import { ItemOrderStatusEnum } from "modules/ItemOrderStatusEnum";
import { normaliseWholeNumber } from "modules/Normalise";
import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Form, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

const CreateItemOrder = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const { state } = useStateMachine();
  const { id, date } = useParams();
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue,
    watch
  } = useForm<ICreateItemOrderRequestModel>({
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [createItemOrderButtonDisabled, setCreateItemOrderButtonDisabled] = useState<boolean>(true);
  const [showUnauthorised, setShowUnauthorised] = useState<boolean>(false);

  const {
    isSending: createItemOrderIsSending, errors: createItemOrderErrors, statusCode: createItemOrderStatusCode, sendRequest: createItemOrderSendRequest
  } = useAmplifyREST();

  const {
    isSending: siteListIsSending, errors: siteListErrors, data: siteListData, sendRequest: siteListSendRequest
  } = useAmplifyREST();

  const isSending = createItemOrderIsSending || siteListIsSending;
  const apiErrors = createItemOrderErrors || siteListErrors;
  const initialApiLoadComplete = siteListData;
  const unauthorised : boolean = id && siteListData && state.createItemOrderPageOrigination! === RoutePathsEnum.SiteDiaryLandingPage
  && !(siteListData.hwrcSites as IHwrcSite[]).find(site => site.id === id);

  if (state.createItemOrderPageOrigination!) {
    useEffect(() => {
      siteListSendRequest("settings/hwrcsites/active", "GET");
    }, [siteListSendRequest]);
  }

  const renderHwrcSites = () => {
    const hwrcSites = [
      <option key="" value="">Please Select</option>
    ];

    if (siteListData) {
      (siteListData.hwrcSites as IHwrcSite[]).forEach(hwrcSite => {
        hwrcSites.push(<option key={hwrcSite.id} value={hwrcSite.id}>{hwrcSite.name}</option>);
      });
    }

    return hwrcSites;
  };

  const renderHwrcSiteName = () => {
    let siteName : string = "";

    if (siteListData && id) {
      siteName = (siteListData.hwrcSites as IHwrcSite[]).find(hwrcSite => hwrcSite.id === id)?.name!;
    }

    return siteName;
  };

  const renderOrderTypes = () => {
    const orderTypes = [
      <option key="" value="">Please Select</option>
    ];

    config.itemOrderTypes.forEach(itemType => orderTypes.push(<option key={itemType} value={itemType}>{itemType}</option>));
    return orderTypes;
  };

  const watchFields = watch(["hwrcSiteId", "orderType", "item", "quantity", "recipient"]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const unauthorisedAlertCloseHandler = useCallback(() => {
    setShowUnauthorised(false);
  }, [setShowUnauthorised]);

  const canFormBeSubmitted = () => {
    if (!getValues("hwrcSiteId")) {
      return;
    }

    const hwrcSiteId = getValues("hwrcSiteId");
    const orderType = getValues("orderType");
    const orderTypeCorrect = config.itemOrderTypes.find(x => x === orderType) !== undefined;
    const quantity = getValues("quantity");
    const quantityCorrect = quantity?.toString().length > 0 && Number(quantity) > 0;

    setCreateItemOrderButtonDisabled(
      !(hwrcSiteId.length > 0
        && (orderTypeCorrect)
        && (quantityCorrect)
        && getValues("item").length > 0
        && getValues("recipient").length > 0
      )
    );
  };

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  const handleCreateItemOrderClick = (request: ICreateItemOrderRequestModel) => {
    setShowExceptions(true);
    if (state.createItemOrderPageOrigination === RoutePathsEnum.SiteDiaryLandingPage) {
      request.hwrcSiteId = id!;
    }
    createItemOrderSendRequest("itemorders", "POST", request);
  };

  const handleNavigate = () => {
    if (state.createItemOrderPageOrigination! === RoutePathsEnum.ItemOrders) {
      navigate(state.createItemOrderPageOrigination!);
    } else {
      navigate(`${state.createItemOrderPageOrigination!}/${id}/${date}`);
    }
  };

  useEffect(() => {
    if (createItemOrderStatusCode === 200) {
      handleNavigate();
    }
  }, [createItemOrderStatusCode]);

  useEffect(() => {
    if (unauthorised) {
      setShowUnauthorised(true);
    }
  }, [unauthorised]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      {unauthorised
        && showUnauthorised && (
        <AmplifyError
          errors={["Unauthorised access for User"]}
          isDismissable
          onCloseHandler={unauthorisedAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h2>
            Create Item Order
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h2>
        </Card.Header>
        {!unauthorised && (
          <Form autoComplete="off" onSubmit={handleSubmit(handleCreateItemOrderClick)}>
            <Card.Body className="no-padding">
              <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
                <Col xs={12} lg={8} className="d-flex">
                  {state.createItemOrderPageOrigination! && (
                    <Breadcrumb className="left-margin-large no-wrap">
                      <Breadcrumb.Item
                        data-testid={state.createItemOrderPageOrigination! === RoutePathsEnum.ItemOrders
                          ? "breadcrumb-item-orders" : "breadcrumb-site-diary"}
                        linkAs={Link}
                        linkProps={{ to: state.createItemOrderPageOrigination! }}
                        onClick={() => handleNavigate()}
                      >
                        {state.createItemOrderPageOrigination! === RoutePathsEnum.ItemOrders ? "Item Orders" : "Site Diary"}
                      </Breadcrumb.Item>

                      <Breadcrumb.Item data-testid="breadcrumb-create-item-order" active>
                        Create Item Order
                      </Breadcrumb.Item>
                    </Breadcrumb>
                  ) }
                </Col>
                <Col className="d-lg-flex justify-content-end user-filter">
                  <IconButton
                    iconType={IconButtonType.Save}
                    variant="primary"
                    className="right-margin-small"
                    disabled={createItemOrderButtonDisabled || isSending}
                    aria-disabled={createItemOrderButtonDisabled || isSending}
                    aria-label="Create item order"
                    type="submit"
                    spin={createItemOrderIsSending}
                    data-testid="buttonCreateItemOrder"
                  >
                    Create
                  </IconButton>
                  <Button
                    variant="secondary"
                    onClick={() => handleNavigate()}
                    data-testid="buttonCancel"
                    aria-label={`Cancel create item order and back to ${state.createItemOrderPageOrigination! === RoutePathsEnum.ItemOrders
                      ? "item orders"
                      : "site diary"}`}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
              <Divider />
              {initialApiLoadComplete
            && (
              <section className="pt-2">
                <Row className="mt-1 ms-1">
                  <Col xs={12} md={8}>
                    {state.createItemOrderPageOrigination! === RoutePathsEnum.ItemOrders && (
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="hwrcSiteId">
                          <Form.Label id="siteLabel" className="horiz-form-label">
                            Site Name:
                          </Form.Label>
                          <Col>
                            <Form.Select
                              {...register("hwrcSiteId", { onChange: () => canFormBeSubmitted() })}
                              data-testid="ddlHwrcSite"
                              aria-required
                              aria-invalid={false}
                              aria-labelledby="hwrcSiteLabel"
                            >
                              {renderHwrcSites()}
                            </Form.Select>
                          </Col>
                          <ErrorMessages fieldName="hwrcSiteId" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                    )}
                    {state.createItemOrderPageOrigination! === RoutePathsEnum.SiteDiaryLandingPage && (
                      <Row className="mb-2">
                        <Form.Control
                          type="hidden"
                          value={id}
                          {...register("hwrcSiteId")}
                        />
                        <Form.Group as={Row} controlId="hwrcSiteId">
                          <Form.Label id="siteLabel" className="horiz-form-label">
                            Site Name:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              aria-required
                              aria-invalid={false}
                              aria-labelledby="hwrcSiteLabel"
                              maxLength={50}
                              disabled
                              value={renderHwrcSiteName()}
                            />
                          </Col>
                          <ErrorMessages fieldName="hwrcSiteId" errors={errors} validationFailures={null} />
                        </Form.Group>
                      </Row>
                    )}
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="orderType">
                        <Form.Label
                          id="typeLabel"
                          className="horiz-form-label"
                        >
                          Type:
                        </Form.Label>
                        <Col>
                          <Form.Select
                            {...register("orderType", { onChange: () => canFormBeSubmitted() })}
                            data-testid="ddlOrderType"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="orderTypeLabel"
                          >
                            {renderOrderTypes()}
                          </Form.Select>
                        </Col>
                        <ErrorMessages
                          fieldName="orderType"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="item">
                        <Form.Label
                          id="itemLabel"
                          className="horiz-form-label"
                        >
                          Item:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="itemLabel"
                            maxLength={50}
                            {...register("item")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="item"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="quantity">
                        <Form.Label
                          id="quantityLabel"
                          className="horiz-form-label"
                        >
                          Quantity:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="quantityLabel"
                            maxLength={6}
                            defaultValue={1}
                            {...register("quantity")}
                            onChange={e => { e.target.value = normaliseWholeNumber(e.target.value); setValue("quantity", e.target.value); }}
                            onKeyUp={() => { canFormBeSubmitted(); }}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="quantity"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="size">
                        <Form.Label
                          id="sizeLabel"
                          className="horiz-form-label"
                        >
                          Size Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={false}
                            aria-labelledby="sizeLabel"
                            maxLength={50}
                            {...register("size")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="size"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="recipient">
                        <Form.Label
                          id="recipientLabel"
                          className="horiz-form-label"
                        >
                          Recipient:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="recipientLabel"
                            maxLength={50}
                            {...register("recipient")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="recipient"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="status">
                        <Form.Label
                          id="statusLabel"
                          className="horiz-form-label"
                        >
                          Status:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="statusLabel"
                            maxLength={50}
                            disabled
                            value={ItemOrderStatusEnum.Raised}
                          />
                          <ErrorMessages
                            fieldName="status"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Col>
                      </Form.Group>
                    </Row>
                  </Col>
                </Row>
              </section>
            )}
            </Card.Body>
          </Form>
        )}
      </Card>
    </Page>
  );
};

export default CreateItemOrder;
